import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  PageHeader,
  Table,
  Drawer,
  SearchBar,
  FilterBar,
  BadgePill,
  EmptyState,
} from '@/components/campus';
import { PassDetailDrawer } from '@/components/pass';
import {
  Plus,
  UserPlus,
  Mail,
  Phone,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  QrCode,
  Clock,
  UserCheck,
  Loader2,
  MoreHorizontal,
  Eye,
  Shield,
  Link2,
  Ticket,
  Trash2,
  Building2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const approvalColors = {
  pending: 'warning',
  approved: 'success',
  denied: 'error',
  waitlisted: 'default',
};

const guestTypeColors = {
  invited: 'primary',
  plus_one: 'default',
  walk_up: 'warning',
  vip: 'success',
};

// Format helpers
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Initial form state
const initialFormState = {
  guest_name: '',
  email: '',
  phone: '',
  event_id: '',
  guest_type: 'invited',
};

// Guest type options
const guestTypeOptions = [
  { value: 'invited', label: 'Invited' },
  { value: 'plus_one', label: 'Plus One' },
  { value: 'walk_up', label: 'Walk Up' },
  { value: 'vip', label: 'VIP' },
];

// Approval status filter options
const approvalFilterOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'waitlisted', label: 'Waitlisted' },
];

// Guest type filter options
const guestTypeFilterOptions = [
  { value: '', label: 'All Types' },
  { value: 'invited', label: 'Invited' },
  { value: 'plus_one', label: 'Plus One' },
  { value: 'walk_up', label: 'Walk Up' },
  { value: 'vip', label: 'VIP' },
];

// ==================== ADD GUEST FORM ====================
const AddGuestForm = ({ events, onSubmit, onCancel, isLoading }) => {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.guest_name.trim()) newErrors.guest_name = 'Guest name is required';
    if (!form.event_id) newErrors.event_id = 'Please select an event';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      guest_name: form.guest_name,
      email: form.email || null,
      phone: form.phone || null,
      guest_type: form.guest_type,
      event_id: form.event_id,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Guest Name */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Guest Name *</Label>
        <Input
          value={form.guest_name}
          onChange={(e) => handleChange('guest_name', e.target.value)}
          placeholder="John Smith"
          className="bg-obsidian border-white/[0.08] text-cloud-white"
          data-testid="guest-name-input"
        />
        {errors.guest_name && <p className="text-signal-red text-xs">{errors.guest_name}</p>}
      </div>

      {/* Event Selection */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Event *</Label>
        <Select value={form.event_id} onValueChange={(v) => handleChange('event_id', v)}>
          <SelectTrigger className="bg-obsidian border-white/[0.08] text-cloud-white">
            <SelectValue placeholder="Select an event" />
          </SelectTrigger>
          <SelectContent className="bg-graphite border-white/[0.08]">
            {events.map(event => (
              <SelectItem 
                key={event.id} 
                value={event.id} 
                className="text-cloud-white hover:bg-white/[0.05]"
              >
                {event.event_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.event_id && <p className="text-signal-red text-xs">{errors.event_id}</p>}
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Email</Label>
        <Input
          type="email"
          value={form.email}
          onChange={(e) => handleChange('email', e.target.value)}
          placeholder="john@university.edu"
          className="bg-obsidian border-white/[0.08] text-cloud-white"
          data-testid="guest-email-input"
        />
        {errors.email && <p className="text-signal-red text-xs">{errors.email}</p>}
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Phone</Label>
        <Input
          type="tel"
          value={form.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          placeholder="(555) 123-4567"
          className="bg-obsidian border-white/[0.08] text-cloud-white"
          data-testid="guest-phone-input"
        />
      </div>

      {/* Guest Type */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Guest Type</Label>
        <Select value={form.guest_type} onValueChange={(v) => handleChange('guest_type', v)}>
          <SelectTrigger className="bg-obsidian border-white/[0.08] text-cloud-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-graphite border-white/[0.08]">
            {guestTypeOptions.map(type => (
              <SelectItem 
                key={type.value} 
                value={type.value} 
                className="text-cloud-white hover:bg-white/[0.05]"
              >
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 border-white/[0.08] text-soft-silver hover:text-cloud-white"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
          disabled={isLoading}
          data-testid="guest-submit-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Adding...
            </>
          ) : 'Add Guest'}
        </Button>
      </div>
    </form>
  );
};

// ==================== GUEST DETAIL DRAWER ====================
const GuestDetailDrawer = ({ 
  guest, 
  isOpen, 
  onClose, 
  onApprove, 
  onDeny,
  onDelete,
  onGeneratePass,
  onViewPass,
  canManage,
  isProcessing 
}) => {
  if (!guest) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Guest Details" width="lg">
      <div className="space-y-6">
        {/* Guest Header */}
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
            <User className="w-7 h-7 text-electric-ice-blue" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-medium text-cloud-white">{guest.guest_name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <BadgePill variant={approvalColors[guest.approval_status]}>
                {guest.approval_status?.toUpperCase()}
              </BadgePill>
              <BadgePill variant={guestTypeColors[guest.guest_type]}>
                {guest.guest_type?.replace('_', ' ').toUpperCase()}
              </BadgePill>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="space-y-3">
          {guest.email && (
            <div className="flex items-center gap-3 text-soft-silver">
              <Mail className="w-4 h-4 text-muted-text" />
              <span>{guest.email}</span>
            </div>
          )}
          {guest.phone && (
            <div className="flex items-center gap-3 text-soft-silver">
              <Phone className="w-4 h-4 text-muted-text" />
              <span className="font-mono">{guest.phone}</span>
            </div>
          )}
        </div>

        {/* Event Info */}
        <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-electric-ice-blue" />
            <div>
              <p className="text-sm text-muted-text">Event</p>
              <p className="text-cloud-white">{guest.event_name || 'Unknown Event'}</p>
            </div>
          </div>
        </div>

        {/* Invited By */}
        {guest.invited_by_name && (
          <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
            <div className="flex items-center gap-3">
              <UserCheck className="w-5 h-5 text-cool-mint" />
              <div>
                <p className="text-sm text-muted-text">Invited By</p>
                <p className="text-cloud-white">{guest.invited_by_name}</p>
              </div>
            </div>
          </div>
        )}

        {/* Invite Source */}
        {guest.invite_source && (
          <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
            <div className="flex items-center gap-3">
              <Link2 className="w-5 h-5 text-soft-amber" />
              <div>
                <p className="text-sm text-muted-text">Invite Source</p>
                <p className="text-cloud-white font-mono text-sm">{guest.invite_source}</p>
              </div>
            </div>
          </div>
        )}

        {/* QR Pass Section */}
        <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <QrCode className="w-5 h-5 text-electric-ice-blue" />
              <div>
                <p className="text-sm text-muted-text">QR Pass</p>
                {guest.has_pass ? (
                  <p className="text-cool-mint">Pass Generated</p>
                ) : guest.approval_status === 'approved' ? (
                  <p className="text-soft-amber">Ready to generate</p>
                ) : (
                  <p className="text-muted-text">Pending approval</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {guest.has_pass && (
                <Button
                  size="sm"
                  onClick={() => onViewPass(guest)}
                  className="bg-cool-mint/10 text-cool-mint border border-cool-mint/20"
                  disabled={isProcessing}
                  data-testid="view-pass-btn"
                >
                  <Ticket className="w-4 h-4 mr-1" />
                  View Pass
                </Button>
              )}
              {guest.approval_status === 'approved' && !guest.has_pass && canManage && (
                <Button
                  size="sm"
                  onClick={() => onGeneratePass(guest.id)}
                  className="bg-electric-ice-blue/10 text-electric-ice-blue border border-electric-ice-blue/20"
                  disabled={isProcessing}
                  data-testid="generate-pass-btn"
                >
                  Generate Pass
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Check-in Status */}
        <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-electric-ice-blue" />
            <div>
              <p className="text-sm text-muted-text">Check-in Status</p>
              {guest.checked_in ? (
                <p className="text-cool-mint">Checked In</p>
              ) : (
                <p className="text-muted-text">Not checked in yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="text-xs text-muted-text space-y-1">
          <p>Added: <span className="font-mono">{formatDateTime(guest.created_at)}</span></p>
          {guest.updated_at !== guest.created_at && (
            <p>Updated: <span className="font-mono">{formatDateTime(guest.updated_at)}</span></p>
          )}
        </div>

        {/* Approval Actions */}
        {canManage && guest.approval_status === 'pending' && (
          <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
            <Button
              onClick={() => onDeny(guest.id)}
              variant="outline"
              className="flex-1 border-signal-red/30 text-signal-red hover:bg-signal-red/10"
              disabled={isProcessing}
              data-testid="deny-guest-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Deny
            </Button>
            <Button
              onClick={() => onApprove(guest.id)}
              className="flex-1 bg-cool-mint text-obsidian hover:bg-cool-mint/90"
              disabled={isProcessing}
              data-testid="approve-guest-btn"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Approve
            </Button>
          </div>
        )}

        {/* Already processed status */}
        {guest.approval_status === 'approved' && (
          <div className="flex items-center gap-2 p-3 bg-cool-mint/10 rounded-lg border border-cool-mint/20">
            <CheckCircle className="w-5 h-5 text-cool-mint" />
            <span className="text-cool-mint">Guest is approved</span>
          </div>
        )}
        {guest.approval_status === 'denied' && (
          <div className="flex items-center gap-2 p-3 bg-signal-red/10 rounded-lg border border-signal-red/20">
            <XCircle className="w-5 h-5 text-signal-red" />
            <span className="text-signal-red">Guest was denied</span>
          </div>
        )}

        {/* Delete Button */}
        {canManage && (
          <div className="pt-4 border-t border-white/[0.06]">
            <Button
              onClick={() => onDelete(guest.id)}
              variant="outline"
              className="w-full border-signal-red/30 text-signal-red hover:bg-signal-red/10"
              data-testid="delete-guest-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Guest
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  );
};

// ==================== MAIN GUESTS PAGE ====================
export const Guests = () => {
  const { organization, session } = useAuth();
  const { hasOrgRole, isDoorStaff } = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if user can manage guests
  const canManage = hasOrgRole(['owner', 'admin', 'event_manager']);

  // State
  const [guests, setGuests] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEventId, setSelectedEventId] = useState(searchParams.get('event_id') || '');
  const [approvalFilter, setApprovalFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pageSize: 50 });
  
  // Pass drawer state
  const [isPassDrawerOpen, setIsPassDrawerOpen] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [passGuest, setPassGuest] = useState(null);

  // Fetch events for dropdown
  const fetchEvents = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/events?organization_id=${organization.id}&page_size=100`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
        
        // Set default event if none selected and events exist
        if (!selectedEventId && data.events?.length > 0) {
          // Find most recent event
          const sorted = [...data.events].sort((a, b) => 
            new Date(b.start_time) - new Date(a.start_time)
          );
          if (sorted[0]) {
            setSelectedEventId(sorted[0].id);
          }
        }
        
        // If no events, stop loading
        if (!data.events?.length) {
          setLoading(false);
        }
      } else {
        console.error('Failed to fetch events:', res.status);
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      setLoading(false);
    }
  }, [organization?.id, session?.access_token, selectedEventId]);

  // Fetch guests
  const fetchGuests = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    // Need an event selected to fetch guests
    if (!selectedEventId) {
      setGuests([]);
      setLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams({
        event_id: selectedEventId,
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
      });
      
      if (approvalFilter) params.append('approval_status', approvalFilter);
      if (typeFilter) params.append('guest_type', typeFilter);

      const res = await fetch(`${API_URL}/api/guests?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        const data = await res.json();
        // Enrich guests with event name
        const event = events.find(e => e.id === selectedEventId);
        const enrichedGuests = (data.guests || []).map(g => ({
          ...g,
          event_name: event?.event_name || 'Unknown Event',
        }));
        setGuests(enrichedGuests);
        setPagination(prev => ({ ...prev, total: data.total || 0 }));
      }
    } catch (error) {
      console.error('Failed to fetch guests:', error);
      toast.error('Failed to load guests');
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token, selectedEventId, approvalFilter, typeFilter, pagination.page, pagination.pageSize, events]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (events.length > 0) {
      fetchGuests();
    }
  }, [fetchGuests, events.length, selectedEventId]);

  // Update URL when event changes
  useEffect(() => {
    if (selectedEventId) {
      setSearchParams({ event_id: selectedEventId });
    }
  }, [selectedEventId, setSearchParams]);

  // Filter guests by search
  const filteredGuests = guests.filter(guest => 
    guest.guest_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    guest.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    guest.phone?.includes(searchQuery)
  );

  // Add guest
  const handleAddGuest = async (data) => {
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/guests?event_id=${data.event_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          guest_name: data.guest_name,
          email: data.email,
          phone: data.phone,
          guest_type: data.guest_type,
        }),
      });

      if (res.ok) {
        toast.success('Guest added successfully');
        setIsFormOpen(false);
        // Switch to the event we just added to
        if (data.event_id !== selectedEventId) {
          setSelectedEventId(data.event_id);
        } else {
          fetchGuests();
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add guest');
      }
    } catch (error) {
      toast.error('Failed to add guest');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Approve guest
  const handleApprove = async (guestId) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/guests/${guestId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success('Guest approved');
        setIsDetailOpen(false);
        fetchGuests();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to approve guest');
      }
    } catch (error) {
      toast.error('Failed to approve guest');
    } finally {
      setIsProcessing(false);
    }
  };

  // Deny guest
  const handleDeny = async (guestId) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/guests/${guestId}/deny`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success('Guest denied');
        setIsDetailOpen(false);
        fetchGuests();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to deny guest');
      }
    } catch (error) {
      toast.error('Failed to deny guest');
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete guest
  const handleDeleteGuest = async (guestId) => {
    if (!window.confirm('Are you sure you want to delete this guest? This action cannot be undone.')) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/guests/${guestId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success('Guest deleted successfully');
        setIsDetailOpen(false);
        setSelectedGuest(null);
        fetchGuests();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete guest');
      }
    } catch (error) {
      toast.error('Failed to delete guest');
    }
  };

  // Generate pass
  const handleGeneratePass = async (guestId) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/passes/generate?guest_id=${guestId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        const passData = await res.json();
        toast.success('Pass generated');
        fetchGuests();
        // Update selected guest
        if (selectedGuest?.id === guestId) {
          setSelectedGuest(prev => ({ ...prev, has_pass: true }));
        }
        // Open the pass drawer to show the generated pass
        const guest = guests.find(g => g.id === guestId) || selectedGuest;
        if (guest) {
          setPassGuest(guest);
          setSelectedPass(passData);
          setIsPassDrawerOpen(true);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to generate pass');
      }
    } catch (error) {
      toast.error('Failed to generate pass');
    } finally {
      setIsProcessing(false);
    }
  };

  // View pass for a guest
  const handleViewPass = async (guest) => {
    if (!guest.has_pass) {
      toast.error('Guest does not have a pass');
      return;
    }
    
    setIsProcessing(true);
    try {
      // Fetch passes for this event to find the guest's pass
      const res = await fetch(
        `${API_URL}/api/passes?event_id=${guest.event_id || selectedEventId}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        const guestPass = data.passes?.find(p => p.guest_id === guest.id && p.status !== 'revoked');
        
        if (guestPass) {
          setPassGuest(guest);
          setSelectedPass(guestPass);
          setIsPassDrawerOpen(true);
        } else {
          toast.error('Pass not found');
        }
      } else {
        toast.error('Failed to fetch pass details');
      }
    } catch (error) {
      toast.error('Failed to fetch pass details');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle pass revoke callback
  const handlePassRevoked = () => {
    fetchGuests();
    // Update the guest's has_pass status
    if (passGuest) {
      setGuests(prev => prev.map(g => 
        g.id === passGuest.id ? { ...g, has_pass: false } : g
      ));
    }
  };

  // Handle pass regenerate callback
  const handlePassRegenerated = () => {
    fetchGuests();
  };

  // View guest detail
  const handleViewGuest = (guest) => {
    setSelectedGuest(guest);
    setIsDetailOpen(true);
  };

  // Table columns
  const columns = [
    {
      key: 'guest_name',
      label: 'Guest',
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
            <User className="w-4 h-4 text-electric-ice-blue" />
          </div>
          <div>
            <p className="font-medium text-cloud-white">{row.guest_name}</p>
            {row.email && <p className="text-xs text-muted-text">{row.email}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'event_name',
      label: 'Event',
      render: (value) => (
        <span className="text-soft-silver">{value || 'Unknown'}</span>
      ),
    },
    {
      key: 'guest_type',
      label: 'Type',
      align: 'center',
      render: (value) => (
        <BadgePill variant={guestTypeColors[value] || 'default'}>
          {value?.replace('_', ' ').toUpperCase()}
        </BadgePill>
      ),
    },
    {
      key: 'approval_status',
      label: 'Status',
      align: 'center',
      render: (value) => (
        <BadgePill variant={approvalColors[value] || 'default'}>
          {value?.toUpperCase()}
        </BadgePill>
      ),
    },
    {
      key: 'has_pass',
      label: 'Pass',
      align: 'center',
      render: (value, row) => (
        <div className="flex justify-center">
          {value ? (
            <QrCode className="w-5 h-5 text-cool-mint" />
          ) : row.approval_status === 'approved' ? (
            <QrCode className="w-5 h-5 text-soft-amber" />
          ) : (
            <QrCode className="w-5 h-5 text-muted-text/30" />
          )}
        </div>
      ),
    },
    {
      key: 'checked_in',
      label: 'Check-in',
      align: 'center',
      render: (value) => (
        <div className="flex justify-center">
          {value ? (
            <CheckCircle className="w-5 h-5 text-cool-mint" />
          ) : (
            <Clock className="w-5 h-5 text-muted-text/30" />
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      sortable: false,
      render: (_, row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="w-4 h-4 text-muted-text" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-graphite border-white/[0.08]">
            <DropdownMenuItem 
              onClick={() => handleViewGuest(row)}
              className="text-cloud-white hover:bg-white/[0.05]"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
            {row.has_pass && (
              <DropdownMenuItem 
                onClick={() => handleViewPass(row)}
                className="text-cool-mint hover:bg-white/[0.05]"
              >
                <Ticket className="w-4 h-4 mr-2" />
                View Pass
              </DropdownMenuItem>
            )}
            {canManage && row.approval_status === 'approved' && !row.has_pass && (
              <DropdownMenuItem 
                onClick={() => handleGeneratePass(row.id)}
                className="text-electric-ice-blue hover:bg-white/[0.05]"
              >
                <QrCode className="w-4 h-4 mr-2" />
                Generate Pass
              </DropdownMenuItem>
            )}
            {canManage && row.approval_status === 'pending' && (
              <>
                <DropdownMenuItem 
                  onClick={() => handleApprove(row.id)}
                  className="text-cool-mint hover:bg-white/[0.05]"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleDeny(row.id)}
                  className="text-signal-red hover:bg-white/[0.05]"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Deny
                </DropdownMenuItem>
              </>
            )}
            {canManage && (
              <DropdownMenuItem 
                onClick={() => handleDeleteGuest(row.id)}
                className="text-signal-red hover:bg-signal-red/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Guest
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // Filter config
  const filters = [
    {
      key: 'approval_status',
      label: 'Status',
      options: approvalFilterOptions,
    },
    {
      key: 'guest_type',
      label: 'Type',
      options: guestTypeFilterOptions,
    },
  ];

  const activeFilters = [
    ...(approvalFilter ? [{ key: 'approval_status', label: 'Status', value: approvalFilter }] : []),
    ...(typeFilter ? [{ key: 'guest_type', label: 'Type', value: typeFilter }] : []),
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading guests...</p>
        </div>
      </div>
    );
  }

  // No organization context
  if (!organization?.id) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-soft-amber/10 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-soft-amber" />
          </div>
          <h2 className="text-xl font-semibold text-cloud-white">No Organization Selected</h2>
          <p className="text-soft-silver">
            Please select an organization from the switcher in the sidebar to manage guests.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="guests-page">
      {/* Page Header */}
      <PageHeader
        title="Guests"
        subtitle={`${pagination.total} guests`}
        actions={
          canManage && (
            <Button
              onClick={() => setIsFormOpen(true)}
              className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
              data-testid="add-guest-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Guest
            </Button>
          )
        }
      />

      {/* Event Selector */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="w-full sm:w-64">
          <Select value={selectedEventId} onValueChange={setSelectedEventId}>
            <SelectTrigger className="bg-graphite border-white/[0.08] text-cloud-white">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-text" />
                <SelectValue placeholder="Select event" />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-graphite border-white/[0.08]">
              {events.map(event => (
                <SelectItem 
                  key={event.id} 
                  value={event.id} 
                  className="text-cloud-white hover:bg-white/[0.05]"
                >
                  {event.event_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex-1">
          <SearchBar
            placeholder="Search guests..."
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </div>
        
        <FilterBar
          filters={filters}
          activeFilters={activeFilters}
          onFilterChange={(key, value) => {
            if (key === 'approval_status') setApprovalFilter(value || '');
            if (key === 'guest_type') setTypeFilter(value || '');
          }}
          onClearAll={() => { setApprovalFilter(''); setTypeFilter(''); }}
        />
      </div>

      {/* Guests Table */}
      {events.length === 0 ? (
        <div className="py-12">
          <EmptyState
            icon="events"
            title="No events yet"
            description="Create an event first to start managing guests."
            actionLabel="Create Event"
            onAction={() => navigate('/app/events')}
          />
        </div>
      ) : !selectedEventId ? (
        <div className="py-12">
          <EmptyState
            icon="users"
            title="Select an event"
            description="Choose an event above to view its guest list."
          />
        </div>
      ) : (
        <Table
          columns={columns}
          data={filteredGuests}
          emptyState={
            <div className="py-12">
              <EmptyState
                icon="users"
                title="No guests yet"
                description="Add guests to this event to start managing your guest list."
                actionLabel={canManage ? "Add Guest" : undefined}
                onAction={canManage ? () => setIsFormOpen(true) : undefined}
              />
            </div>
          }
        />
      )}

      {/* Add Guest Drawer */}
      <Drawer
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title="Add Guest"
        width="md"
      >
        <AddGuestForm
          events={events}
          onSubmit={handleAddGuest}
          onCancel={() => setIsFormOpen(false)}
          isLoading={isSubmitting}
        />
      </Drawer>

      {/* Guest Detail Drawer */}
      <GuestDetailDrawer
        guest={selectedGuest}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onApprove={handleApprove}
        onDeny={handleDeny}
        onDelete={handleDeleteGuest}
        onGeneratePass={handleGeneratePass}
        onViewPass={handleViewPass}
        canManage={canManage}
        isProcessing={isProcessing}
      />

      {/* Pass Detail Drawer */}
      <PassDetailDrawer
        pass={selectedPass}
        guest={passGuest}
        event={events.find(e => e.id === (passGuest?.event_id || selectedEventId))}
        isOpen={isPassDrawerOpen}
        onClose={() => {
          setIsPassDrawerOpen(false);
          setSelectedPass(null);
          setPassGuest(null);
        }}
        onRevoke={handlePassRevoked}
        onRegenerate={handlePassRegenerated}
        session={session}
      />
    </div>
  );
};

export default Guests;
